import { useEffect, useState } from "react";
import api from "../lib/api";
import { ChatInterface } from "../component/ChatInterface";
import { AppSidebar } from "../component/AppSidebar";
import { ResumeUpload } from "../component/ResumeUpload";
import { SidebarProvider, SidebarTrigger } from "../component/ui/sidebar";
import { Badge } from "../component/ui/badge";
import { Button } from "../component/ui/button";
import { FileText, User } from "lucide-react";
import { toast } from "sonner";

const CareerCoach = () => {
  const [activeSession, setActiveSession] = useState(null);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [userName, setUserName] = useState("");

  // Initialize: check token, ensure profile, load data
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check if token is valid
        // if (!api.isTokenValid()) {
        //   api.redirectToLogin();
        //   return;
        // }

        // Ensure profile exists (creates if not)
        await api.ensureProfile();

        // Fetch user profile for name
        try {
          const data = await api.getProfile();
          let profile = data.profile;
          if (profile?.full_name) {
            const firstName = profile.full_name.split(" ")[0];
            setUserName(firstName);
          }
        } catch (error) {
          console.error("Error fetching profile:", error);
        }

        // Initialize session and check resume
        await initializeSession();
        await checkResume();
      } catch (error) {
        console.error("Initialization error:", error);
        toast.error("Failed to initialize. Please try again.");
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, []);

  const checkResume = async () => {
    try {
      const data = await api.getLatestResume();
      console.log("resume Data", data.data.resume)
      if (data.data.resume) {
        setHasResume(true);
        setResumeData(data.data.resume);
      }
    } catch (error) {
      // No resume found is not an error
      console.log("No resume found:", error.message);
    }
  };

  const initializeSession = async () => {
    try {
      // Try to get active session first
      const activeSessionData = await api.getSessions();
      if (activeSessionData.data.sessions.length > 0) {
        // console.log("Active session found:", activeSessionData.data.sessions);
        setActiveSession(activeSessionData.data.sessions[0]);
        return;
      }
    } catch (error) {
      // No active session, create new one
      console.log("No active session, creating new one");
    }

    await createNewSession();
  };

  const createNewSession = async () => {
    try {
      const data = await api.createSession("general");
      if (data) {
        // console.log("Active session found:", data.data.session);
        setActiveSession(data.data.session);
      }
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Failed to create new session");
    }
  };

  const handleSelectSession = async (sessionId) => {
    try {
      const data = await api.getSession(sessionId);
      // console.log("Selected session:", data.data.session);
      if (data.data.session) {
        setActiveSession(data.data.session);
      }
    } catch (error) {
      console.error("Error selecting session:", error);
      toast.error("Failed to load session");
    }
  };

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Initializing...</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-background overflow-hidden">
        <AppSidebar
          activeSessionId={activeSession?.id || null}
          onNewChat={createNewSession}
          onSelectSession={handleSelectSession}
          onUploadResume={() => setShowResumeUpload(true)}
        />

        <main className="flex-1 flex flex-col h-screen overflow-hidden">
          <header className="flex-shrink-0 border-b bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/30">
            <div className="flex h-14 md:h-16 items-center px-3 md:px-4 gap-2 md:gap-4 justify-between">
              <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                <SidebarTrigger className="flex-shrink-0" />
                <h1 className="text-base md:text-lg font-semibold truncate">Moil Career Coach</h1>
                {hasResume && (
                  <Badge
                    variant="secondary"
                    className="hidden sm:inline-flex bg-secondary/20 text-secondary border-secondary/30 flex-shrink-0"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    Profile Complete
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowResumeUpload(true)}
                className="gap-1 md:gap-2 flex-shrink-0 text-xs md:text-sm"
              >
                <User className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">{hasResume ? "Edit Profile" : "Set Up Profile"}</span>
                <span className="sm:hidden">Profile</span>
              </Button>
            </div>
          </header>

          <div className="flex-1 overflow-hidden">
            {showResumeUpload ? (
              <div className="h-full overflow-y-auto p-3 md:p-4">
                <ResumeUpload
                  onClose={() => setShowResumeUpload(false)}
                  onUploadComplete={() => {
                    checkResume();
                    setShowResumeUpload(false);
                  }}
                />
              </div>
            ) : activeSession ? (
              <div className="h-full flex flex-col">
                {hasResume && resumeData && (
                  <div className="flex-shrink-0 bg-card border-b md:border md:rounded-lg md:mx-4 md:mt-4 p-3 md:p-4 flex items-start gap-3">
                    <FileText className="h-4 w-4 md:h-5 md:w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-medium text-sm md:text-base">Knowledge Base Active</h3>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          Resume Loaded
                        </Badge>
                      </div>
                      <p className="text-xs md:text-sm text-muted-foreground truncate">
                        {resumeData.file_name} • Last updated{" "}
                        {new Date(resumeData.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex-1 overflow-hidden px-0 md:px-4 pb-0 md:pb-4">
                  <div className="h-full max-w-5xl mx-auto">
                    <ChatInterface
                      sessionId={activeSession.id}
                      resumeData={resumeData?.parsed_data}
                      sessionType={activeSession.session_type}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground text-sm md:text-base">Loading...</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default CareerCoach;
